import React from 'react';

const TrigDiagram = ({ data }) => {
  if (!data) return null;

  const { subtype = 'right_triangle' } = data;
  const svgW = 300, svgH = 280;

  switch (subtype) {
    case 'right_triangle': {
      const {
        angle = 30,
        hypotenuse = {},
        opposite = {},
        adjacent = {},
        rightAngleVertex = 'bottom-right',
        labels = {}
      } = data;

      // Triangle vertices: right angle at bottom-right by default
      // A = bottom-left, B = bottom-right (right angle), C = top-right
      const pad = 40;
      const triW = svgW - pad * 2;
      const triH = svgH - pad * 2 - 20;

      let ax, ay, bx, by, cpx, cpy;
      if (rightAngleVertex === 'bottom-left') {
        ax = pad; ay = svgH - pad;           // A (right angle) bottom-left
        bx = svgW - pad; by = svgH - pad;     // B bottom-right
        cpx = pad; cpy = pad + 20;             // C top-left
      } else {
        // Default: right angle at bottom-right
        ax = pad; ay = svgH - pad;             // A bottom-left (angle θ here)
        bx = svgW - pad; by = svgH - pad;     // B bottom-right (right angle)
        cpx = svgW - pad; cpy = pad + 20;     // C top-right
      }

      // Angle arc at A
      const arcR = 35;
      const angleRad = angle * Math.PI / 180;
      const arcEndX = ax + arcR * Math.cos(0);
      const arcEndY = ay - arcR * Math.sin(0);
      const arcStartX = ax + arcR * Math.cos(angleRad > Math.PI / 2 ? Math.PI / 2 : angleRad);
      const arcStartY = ay - arcR * Math.sin(angleRad > Math.PI / 2 ? Math.PI / 2 : angleRad);

      // Right angle square at B
      const sqSize = 15;
      let sqPath;
      if (rightAngleVertex === 'bottom-left') {
        sqPath = `M${ax + sqSize},${ay} L${ax + sqSize},${ay - sqSize} L${ax},${ay - sqSize}`;
      } else {
        sqPath = `M${bx - sqSize},${by} L${bx - sqSize},${by - sqSize} L${bx},${by - sqSize}`;
      }

      // Label positions
      const hypMidX = (ax + cpx) / 2;
      const hypMidY = (ay + cpy) / 2;
      const dx = cpx - ax, dy = cpy - ay;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const hypOffX = -(dy / len) * 18;
      const hypOffY = (dx / len) * 18;

      return (
        <div className="diagram-container">
          {data.title && <div className="diagram-title">{data.title}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="trig-svg" role="img" aria-label={data.title || 'Right triangle'}>
            {/* Triangle fill */}
            <polygon points={`${ax},${ay} ${bx},${by} ${cpx},${cpy}`}
              fill="rgba(33, 150, 243, 0.08)" stroke="#1976d2" strokeWidth="2" />

            {/* Right angle marker */}
            <path d={sqPath} fill="none" stroke="#e91e63" strokeWidth="1.5" />

            {/* Angle arc at θ vertex */}
            {rightAngleVertex !== 'bottom-left' ? (
              <g>
                <path d={`M${ax + arcR},${ay} A${arcR},${arcR} 0 0 1 ${arcStartX},${arcStartY}`}
                  fill="none" stroke="#e91e63" strokeWidth="1.5" />
                <text x={ax + arcR + 8} y={ay - 12} className="diagram-angle-label">
                  {angle}°
                </text>
              </g>
            ) : (
              <g>
                <path d={`M${bx - arcR},${by} A${arcR},${arcR} 0 0 0 ${bx - arcR * Math.cos(angleRad)},${by - arcR * Math.sin(angleRad)}`}
                  fill="none" stroke="#e91e63" strokeWidth="1.5" />
                <text x={bx - arcR - 12} y={by - 14} textAnchor="end" className="diagram-angle-label">
                  {angle}°
                </text>
              </g>
            )}

            {/* Side labels */}
            {/* Hypotenuse (A to C) */}
            {hypotenuse.label && (
              <text x={hypMidX + hypOffX} y={hypMidY + hypOffY}
                textAnchor="middle" dominantBaseline="middle"
                className="diagram-side-label" fill="#1976d2">{hypotenuse.label}</text>
            )}

            {/* Opposite (B to C — vertical) */}
            {opposite.label && (
              <text x={rightAngleVertex === 'bottom-left' ? ax - 20 : bx + 20}
                y={(by + cpy) / 2}
                textAnchor={rightAngleVertex === 'bottom-left' ? 'end' : 'start'}
                dominantBaseline="middle"
                className="diagram-side-label" fill="#4caf50">{opposite.label}</text>
            )}

            {/* Adjacent (A to B — horizontal) */}
            {adjacent.label && (
              <text x={(ax + bx) / 2} y={ay + 22}
                textAnchor="middle"
                className="diagram-side-label" fill="#ff9800">{adjacent.label}</text>
            )}

            {/* Vertex labels */}
            {labels.A && <text x={ax - 12} y={ay + 6} textAnchor="end" className="diagram-vertex-label">{labels.A}</text>}
            {labels.B && <text x={bx + 12} y={by + 6} textAnchor="start" className="diagram-vertex-label">{labels.B}</text>}
            {labels.C && <text x={cpx + (rightAngleVertex === 'bottom-left' ? -12 : 12)} y={cpy - 6}
              textAnchor={rightAngleVertex === 'bottom-left' ? 'end' : 'start'}
              className="diagram-vertex-label">{labels.C}</text>}
          </svg>
        </div>
      );
    }

    case 'unit_circle': {
      const { angle = 45, showCoords = true, showProjections = true } = data;
      const cx = svgW / 2, cy = svgH / 2;
      const r = 100;
      const angleRad = angle * Math.PI / 180;
      const px = cx + r * Math.cos(angleRad);
      const py = cy - r * Math.sin(angleRad);

      return (
        <div className="diagram-container">
          {data.title && <div className="diagram-title">{data.title}</div>}
          <svg viewBox={`0 0 ${svgW} ${svgH}`} className="trig-svg" role="img" aria-label={data.title || 'Unit circle'}>
            {/* Axes */}
            <line x1={cx - r - 30} y1={cy} x2={cx + r + 30} y2={cy} stroke="#333" strokeWidth="1" />
            <line x1={cx} y1={cy + r + 30} x2={cx} y2={cy - r - 30} stroke="#333" strokeWidth="1" />

            {/* Circle */}
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1976d2" strokeWidth="2" />

            {/* Projections */}
            {showProjections && (
              <g>
                <line x1={px} y1={py} x2={px} y2={cy} stroke="#4caf50" strokeWidth="1.5" strokeDasharray="4,3" />
                <line x1={cx} y1={py} x2={px} y2={py} stroke="#ff9800" strokeWidth="1.5" strokeDasharray="4,3" />
              </g>
            )}

            {/* Radius line */}
            <line x1={cx} y1={cy} x2={px} y2={py} stroke="#e91e63" strokeWidth="2" />
            <circle cx={px} cy={py} r="4" fill="#e91e63" />

            {/* Angle arc */}
            <path d={`M${cx + 30},${cy} A30,30 0 ${angle > 180 ? 1 : 0} 0 ${cx + 30 * Math.cos(angleRad)},${cy - 30 * Math.sin(angleRad)}`}
              fill="none" stroke="#e91e63" strokeWidth="1.5" />
            <text x={cx + 40} y={cy - 8} className="diagram-angle-label">{angle}°</text>

            {/* Labels */}
            <text x={cx + r + 12} y={cy + 4} className="diagram-grid-label">1</text>
            <text x={cx - 4} y={cy - r - 8} textAnchor="middle" className="diagram-grid-label">1</text>
            <text x={cx - r - 8} y={cy + 4} textAnchor="end" className="diagram-grid-label">-1</text>
            <text x={cx - 4} y={cy + r + 16} textAnchor="middle" className="diagram-grid-label">-1</text>

            {/* Sin/Cos labels */}
            {showProjections && (
              <g>
                <text x={px + 6} y={(cy + py) / 2} className="diagram-trig-label" fill="#4caf50">
                  sin {angle}°
                </text>
                <text x={(cx + px) / 2} y={cy + 16} textAnchor="middle" className="diagram-trig-label" fill="#ff9800">
                  cos {angle}°
                </text>
              </g>
            )}

            {/* Coordinates */}
            {showCoords && (
              <text x={px + (angle > 90 && angle < 270 ? -8 : 8)} y={py - 10}
                textAnchor={angle > 90 && angle < 270 ? 'end' : 'start'}
                className="diagram-coord-label">
                ({Math.cos(angleRad).toFixed(2)}; {Math.sin(angleRad).toFixed(2)})
              </text>
            )}

            {/* Origin */}
            <circle cx={cx} cy={cy} r="2.5" fill="#333" />
            <text x={cx - 10} y={cy + 14} className="diagram-grid-label">O</text>
          </svg>
        </div>
      );
    }

    case 'angle_of_elevation':
    case 'angle_of_depression': {
      const {
        angle = 35,
        height = {},
        distance = {},
        hypotenuse: hyp = {}
      } = data;

      const isElev = subtype === 'angle_of_elevation';
      const pad = 40;

      // Ground level
      const groundY = svgH - pad;
      const leftX = pad;
      const rightX = svgW - pad;
      const topY = pad + 20;

      if (isElev) {
        // Observer at bottom-left, object at top-right
        return (
          <div className="diagram-container">
            {data.title && <div className="diagram-title">{data.title}</div>}
            <svg viewBox={`0 0 ${svgW} ${svgH}`} className="trig-svg" role="img" aria-label={data.title || 'Angle of elevation'}>
              {/* Ground line */}
              <line x1={leftX - 20} y1={groundY} x2={rightX + 20} y2={groundY}
                stroke="#795548" strokeWidth="2" />
              {/* Building/object */}
              <line x1={rightX} y1={groundY} x2={rightX} y2={topY}
                stroke="#1976d2" strokeWidth="2" />
              {/* Line of sight */}
              <line x1={leftX} y1={groundY} x2={rightX} y2={topY}
                stroke="#e91e63" strokeWidth="2" strokeDasharray="6,4" />
              {/* Horizontal line from observer */}
              <line x1={leftX} y1={groundY} x2={rightX} y2={groundY}
                stroke="#333" strokeWidth="1.5" />
              {/* Right angle */}
              <path d={`M${rightX - 15},${groundY} L${rightX - 15},${groundY - 15} L${rightX},${groundY - 15}`}
                fill="none" stroke="#e91e63" strokeWidth="1.5" />
              {/* Angle arc */}
              <path d={`M${leftX + 45},${groundY} A45,45 0 0 1 ${leftX + 45 * Math.cos(angle * Math.PI / 180)},${groundY - 45 * Math.sin(angle * Math.PI / 180)}`}
                fill="none" stroke="#e91e63" strokeWidth="1.5" />
              <text x={leftX + 55} y={groundY - 14} className="diagram-angle-label">{angle}°</text>
              {/* Labels */}
              {height.label && <text x={rightX + 16} y={(groundY + topY) / 2}
                textAnchor="start" className="diagram-side-label" fill="#1976d2">{height.label}</text>}
              {distance.label && <text x={(leftX + rightX) / 2} y={groundY + 22}
                textAnchor="middle" className="diagram-side-label" fill="#ff9800">{distance.label}</text>}
              {hyp.label && (
                <text x={(leftX + rightX) / 2 - 20} y={(groundY + topY) / 2 - 10}
                  textAnchor="middle" className="diagram-side-label" fill="#e91e63">{hyp.label}</text>
              )}
              {/* Observer dot */}
              <circle cx={leftX} cy={groundY} r="4" fill="#333" />
              {/* Object dot */}
              <circle cx={rightX} cy={topY} r="4" fill="#1976d2" />
            </svg>
          </div>
        );
      } else {
        // Observer at top-left, object at bottom-right
        return (
          <div className="diagram-container">
            {data.title && <div className="diagram-title">{data.title}</div>}
            <svg viewBox={`0 0 ${svgW} ${svgH}`} className="trig-svg" role="img" aria-label={data.title || 'Angle of depression'}>
              {/* Ground line */}
              <line x1={leftX - 20} y1={groundY} x2={rightX + 20} y2={groundY}
                stroke="#795548" strokeWidth="2" />
              {/* Building/cliff */}
              <line x1={leftX} y1={groundY} x2={leftX} y2={topY}
                stroke="#1976d2" strokeWidth="2" />
              {/* Horizontal from observer */}
              <line x1={leftX} y1={topY} x2={rightX} y2={topY}
                stroke="#999" strokeWidth="1" strokeDasharray="4,3" />
              {/* Line of sight */}
              <line x1={leftX} y1={topY} x2={rightX} y2={groundY}
                stroke="#e91e63" strokeWidth="2" strokeDasharray="6,4" />
              {/* Right angle at base */}
              <path d={`M${leftX + 15},${groundY} L${leftX + 15},${groundY - 15} L${leftX},${groundY - 15}`}
                fill="none" stroke="#e91e63" strokeWidth="1.5" />
              {/* Angle arc (depression from horizontal) */}
              <path d={`M${leftX + 45},${topY} A45,45 0 0 0 ${leftX + 45 * Math.cos(angle * Math.PI / 180)},${topY + 45 * Math.sin(angle * Math.PI / 180)}`}
                fill="none" stroke="#e91e63" strokeWidth="1.5" />
              <text x={leftX + 55} y={topY + 20} className="diagram-angle-label">{angle}°</text>
              {/* Labels */}
              {height.label && <text x={leftX - 16} y={(groundY + topY) / 2}
                textAnchor="end" className="diagram-side-label" fill="#1976d2">{height.label}</text>}
              {distance.label && <text x={(leftX + rightX) / 2} y={groundY + 22}
                textAnchor="middle" className="diagram-side-label" fill="#ff9800">{distance.label}</text>}
              {/* Observer dot */}
              <circle cx={leftX} cy={topY} r="4" fill="#333" />
              {/* Object dot */}
              <circle cx={rightX} cy={groundY} r="4" fill="#1976d2" />
            </svg>
          </div>
        );
      }
    }

    default:
      return <div className="diagram-container"><p>Unknown trig diagram: {subtype}</p></div>;
  }
};

export default TrigDiagram;
